"use client";

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";

import { SpecialRow } from "@/data/specials";
import { VideoComedianContext } from "@/data/videoPlayback";

/**
 * Everything the persistent host needs to render the player for one special.
 * A special page pushes this via `setMedia` on mount; the host adopts it (and
 * keeps playing across navigation, which is the whole point).
 */
export type PersistentMedia = {
  special: SpecialRow;
  context: VideoComedianContext;
  userCanAccessSpecial: boolean;
  posterImg: string;
  lastPlaybackLocation: number;
};

type PersistentPlayerContextValue = {
  /** The media the current page wants playing (latest `setMedia`). */
  media: PersistentMedia | null;
  /** The page's hero placeholder element the host positions itself over. */
  slotEl: HTMLElement | null;
  /**
   * The host's actual rendered height (16:9 video + control bar). The slot
   * reserves this so the page chrome sits exactly below the player — a pure
   * 16:9 box would under-reserve by the control-bar height.
   */
  playerHeight: number | null;
  setMedia: (media: PersistentMedia | null) => void;
  registerSlot: (el: HTMLElement | null) => void;
  setPlayerHeight: (h: number | null) => void;
};

const Ctx = createContext<PersistentPlayerContextValue | null>(null);

export const usePersistentPlayer = () => {
  const v = useContext(Ctx);
  if (!v) {
    throw new Error(
      "usePersistentPlayer must be used inside <PersistentPlayerProvider>"
    );
  }
  return v;
};

/**
 * Holds the "what's playing" + "where to render it" state for the
 * navigation-surviving player. Lives in the root layout so it (and the host it
 * feeds) outlive page transitions.
 *
 * NOTE: there is deliberately no client-side gate here. The
 * `autoplay_persistent_player` gate is evaluated ONCE, server-side, in the
 * special page (`page.tsx` → `persistentPlayerEnabled`), which decides whether
 * to render a `<HeroSlot/>` at all. A `<HeroSlot/>` mounting is therefore the
 * single source of truth that the cohort is enabled: it pushes `media` here, and
 * the host renders off `media` presence. Re-evaluating the gate client-side
 * (e.g. `useGate`) risked disagreeing with the server before the Statsig client
 * bootstrapped — server ON + client OFF left the page showing a hero placeholder
 * with no player over it (a poster-only dead box). Trusting the server decision
 * removes that whole failure mode.
 */
export function PersistentPlayerProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [media, setMedia] = useState<PersistentMedia | null>(null);
  const [slotEl, setSlotEl] = useState<HTMLElement | null>(null);
  const [playerHeight, setPlayerHeightState] = useState<number | null>(null);

  const registerSlot = useCallback((el: HTMLElement | null) => {
    setSlotEl(el);
  }, []);

  const setPlayerHeight = useCallback((h: number | null) => {
    // Avoid churn from sub-pixel resize noise.
    setPlayerHeightState((prev) =>
      prev !== null && h !== null && Math.abs(prev - h) < 1 ? prev : h
    );
  }, []);

  const value = useMemo<PersistentPlayerContextValue>(
    () => ({
      media,
      slotEl,
      playerHeight,
      setMedia,
      registerSlot,
      setPlayerHeight,
    }),
    [media, slotEl, playerHeight, registerSlot, setPlayerHeight]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** Re-exported for convenience. */
export type { SpecialRow };
